import React from 'react';
import {
  User,
  Footprints,
  Map,
  ScrollText,
  Landmark,
  Sparkles,
  Compass,
  BookOpen,
} from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen py-8 bg-stone-950 text-stone-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <header className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <User className="h-12 w-12 text-amber-400" />
          </div>
          <p className="text-xs uppercase tracking-[0.35em] text-amber-400 mb-3">
            Author • Researcher • Storyworld Builder
          </p>
          <h1 className="text-3xl md:text-5xl font-bold text-amber-100 mb-4">
            About Roman Storyworld
          </h1>
          <p className="text-base md:text-lg text-stone-300 max-w-3xl mx-auto leading-relaxed">
            Roman Storyworld is the creative home of Martin Antrobus: a place where Roman Britain is explored through fiction, landscape, archaeology, maps, memory, and the long shadow of empire.
          </p>
        </header>

        {/* Author Statement */}
        <section className="bg-gradient-to-r from-stone-900 via-stone-900 to-amber-950 rounded-lg p-5 md:p-8 border border-amber-700 shadow-lg mb-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber-400 hover:shadow-[0_0_30px_rgba(245,158,11,0.25)]">
          <div className="flex items-center mb-4">
            <Sparkles className="h-7 w-7 text-amber-400 mr-3" />
            <h2 className="text-lg font-semibold text-amber-200">The Purpose of Storyworld</h2>
          </div>
          <p className="text-amber-50 mb-4 text-sm md:text-base leading-relaxed">
            Roman Storyworld exists to make the past feel close without making it false. It brings together fiction, historical research, interactive maps, timelines, visual material, and reflective landscape writing so readers can move between story and evidence with confidence.
          </p>
          <p className="text-amber-50 text-sm md:text-base leading-relaxed">
            The aim is simple: to help readers stand in the present and sense the older world beneath it — not as dry background, but as lived human experience.
          </p>
        </section>

        {/* Main Introduction */}
        <section className="bg-stone-900 rounded-lg shadow-lg p-5 md:p-8 mb-8 border border-stone-700 transition-all duration-300 hover:-translate-y-1 hover:border-amber-400 hover:shadow-[0_0_30px_rgba(245,158,11,0.22)]">
          <div className="prose prose-sm max-w-none">
<div className="flex items-center gap-2 mb-3">
  <BookOpen className="h-6 w-6 text-amber-400" />
  <h2 className="text-lg font-semibold text-amber-200">
    About the Author
  </h2>
</div>
            <p className="text-stone-200 leading-relaxed mb-4 text-sm md:text-base">
              Martin writes historical fiction grounded in the physical traces of the past: roads beneath hedgerows, ruined forts, river crossings, hillfort ramparts, ancient trackways, coastal margins, and the quiet marks still left in the land. His work is shaped by research, but also by the act of walking — returning to places where Roman power, tribal identity, violence, survival, and belief once met.
            </p>

            <p className="text-stone-200 leading-relaxed mb-4 text-sm md:text-base">
              The <span className="text-amber-200 font-semibold">Sons of the Standard</span> sequence grows from this meeting point between evidence and imagination. It follows people caught between worlds: Roman soldiers broken by civil war, tribal leaders defending memory and land, captives resisting the machinery of empire, and communities forced to decide what loyalty, freedom, and honour truly mean.
            </p>

            <p className="text-stone-200 leading-relaxed text-sm md:text-base">
              This site is not only a home for the novels. It is a companion landscape — a place to explore the research behind the stories, the maps that shaped them, the characters who carry them, and the real locations that continue to hold their echoes.
            </p>
          </div>
        </section>

        {/* Distinctive About Sections */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          <article className="bg-stone-900 p-5 rounded-lg shadow-md border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <div className="flex items-center mb-3">
              <Footprints className="h-6 w-6 text-amber-400 mr-2" />
              <h2 className="text-lg font-semibold text-amber-200">A Way of Seeing</h2>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              Martin’s approach begins with attention. A road is not just a line on a map. A fort is not just a ruin. A river crossing, a ridge, a burial place, or a scatter of stone can become a doorway into the lives of people who once moved through the same weather, fear, hunger, hope, and uncertainty.
            </p>
          </article>

          <article className="bg-stone-900 p-5 rounded-lg shadow-md border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <div className="flex items-center mb-3">
              <Map className="h-6 w-6 text-amber-400 mr-2" />
              <h2 className="text-lg font-semibold text-amber-200">Evidence and Imagination</h2>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              The research pages, timelines, and maps are built to separate what can be tested from what has been imagined. The fiction grows from real questions — about Roman roads, tribal territories, mines, forts, trade, warfare, and settlement — but the story remains honest about where history ends and invention begins.
            </p>
          </article>

          <article className="bg-stone-900 p-5 rounded-lg shadow-md border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <div className="flex items-center mb-3">
              <ScrollText className="h-6 w-6 text-amber-400 mr-2" />
              <h2 className="text-lg font-semibold text-amber-200">Oath, Memory, and Land</h2>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              Across the storyworld, symbols matter: the eagle, the boar, the torc, the spear grounded in soil, the lake that receives what can no longer be carried. These are not decorations. They are the language of people trying to survive conquest without surrendering their sense of who they are.
            </p>
          </article>

          <article className="bg-stone-900 p-5 rounded-lg shadow-md border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <div className="flex items-center mb-3">
              <Landmark className="h-6 w-6 text-amber-400 mr-2" />
              <h2 className="text-lg font-semibold text-amber-200">Roman Britain from Below</h2>
            </div>
            <p className="text-stone-300 text-sm leading-relaxed">
              The novels look beyond governors, emperors, and campaigns. They ask how ordinary people experienced upheaval: families driven from farms, warriors bound by old loyalties, captive labourers taken to mines, healers caught between sides, and soldiers who discover that obedience and justice are not always the same thing.
            </p>
          </article>
        </section>

        {/* Explore CTA */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a href="/books" className="block bg-stone-900 p-5 rounded-lg border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <BookOpen className="h-6 w-6 text-amber-400 mb-3" />
            <h3 className="text-amber-200 font-semibold mb-2">Read the Novels</h3>
            <p className="text-stone-300 text-sm leading-relaxed">
              Enter the Sons of the Standard sequence through the books and featured story moments.
            </p>
          </a>

          <a href="/characters/timeline" className="block bg-stone-900 p-5 rounded-lg border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]">
            <Compass className="h-6 w-6 text-amber-400 mb-3" />
            <h3 className="text-amber-200 font-semibold mb-2">Follow the Scene Timeline</h3>
            <p className="text-stone-300 text-sm leading-relaxed">
              Follow the trilogy through its key scenes, characters, conflicts, and turning points.
            </p>
          </a>
<a
  href="/research"
  className="block bg-stone-900 p-5 rounded-lg border border-stone-700 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-400 hover:shadow-[0_0_28px_rgba(245,158,11,0.28)]"
>
  <ScrollText className="h-6 w-6 text-amber-400 mb-3" />

  <h3 className="text-amber-200 font-semibold mb-2">
    Explore the Research Archive
  </h3>

  <p className="text-stone-300 text-sm leading-relaxed">
    Search the archive or ask the Research Guide about roads, forts,
    tribes, inscriptions, archaeology, and place.
  </p>
</a>
       
        </section>
      </div>
    </div>
  );
};

export default About;
